import React, { useMemo } from "react";
import {
    FinancialAccountSourceType,
    FinancialAccountType,
} from "@common/types/financialAccount";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerSectionData } from "@design-system/Picker/Picker.types";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import type { EntityAvatarGroupEntity } from "@components/avatar/EntityAvatarGroup";
import { AccountPickerIcon } from "@components/financialAccount/AccountPickerItem";
import { DEFAULT_ICON_COLOR } from "@constants";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import styles from "./AddTransactionsFinancialAccountSelect.module.scss";

interface Props {
    value?: number;
    onChange: (value: {
        financialAccountId?: number;
        entityId?: number;
    }) => void;
}

type BankAccount = ReturnType<typeof useFinancialAccounts>[number];

const accountOptionLabel = (account: BankAccount) =>
    account.financialType === FinancialAccountType.CASH
        ? `${account.name ?? "Cash"} - ${account.entity?.name ?? "Unknown entity"}`
        : (account.name ?? "Cash");

const AccountOptionContent = ({ account }: { account: BankAccount }) => (
    <div className={styles.accountOption}>
        <AccountPickerIcon account={account} />
        <BreakableText
            text={accountOptionLabel(account)}
            className={styles.accountOptionText}
            showTooltip
        />
    </div>
);

export const AddTransactionsFinancialAccountSelect: React.FC<Props> = ({
    value,
    onChange,
}) => {
    const bankAccounts = useFinancialAccounts({ createTempCashAccounts: true });

    const selectedAccount = useMemo(
        () =>
            value
                ? bankAccounts.find(
                      ({ id, sourceType }) =>
                          id === value &&
                          (sourceType === FinancialAccountSourceType.MANUAL ||
                              sourceType ===
                                  FinancialAccountSourceType.FILE_IMPORT),
                  )
                : undefined,
        [bankAccounts, value],
    );

    const accountsByEntity = useMemo(() => {
        const groupedAccounts = new Map<
            number | string,
            {
                entity: EntityAvatarGroupEntity;
                accounts: typeof bankAccounts;
            }
        >();

        bankAccounts
            .filter(
                (account) =>
                    account.sourceType === FinancialAccountSourceType.MANUAL ||
                    account.sourceType ===
                        FinancialAccountSourceType.FILE_IMPORT,
            )
            .forEach((account) => {
                const entityKey = account.entity?.id ?? "unknown";
                const existingGroup = groupedAccounts.get(entityKey);

                if (existingGroup) {
                    groupedAccounts.set(entityKey, {
                        ...existingGroup,
                        accounts: [...existingGroup.accounts, account],
                    });
                    return;
                }

                groupedAccounts.set(entityKey, {
                    entity: account.entity
                        ? {
                              id: account.entity.id,
                              name: account.entity.name,
                              iconColor: account.entity.iconColor,
                              logo: account.entity.logo,
                          }
                        : {
                              id: -1,
                              name: "Unknown entity",
                              iconColor: DEFAULT_ICON_COLOR,
                              logo: null,
                          },
                    accounts: [account],
                });
            });

        return Array.from(groupedAccounts.values()).toSorted((a, b) =>
            (a.entity?.name ?? "").localeCompare(b.entity?.name ?? ""),
        );
    }, [bankAccounts]);

    const sections = useMemo(
        (): Array<PickerSectionData<number>> =>
            accountsByEntity.map(({ entity, accounts }) => ({
                key: String(entity.id),
                title: entity.name ?? undefined,
                icon: <EntityAvatar size="xs" entity={entity} aria-hidden />,
                items: accounts.map((account) => ({
                    value: account.id,
                    label: accountOptionLabel(account),
                    leading: <AccountPickerIcon account={account} />,
                })),
            })),
        [accountsByEntity],
    );

    return (
        <Picker.Select
            sections={sections}
            value={selectedAccount?.id ?? null}
            onValueChange={(accountId) => {
                const selectedBankAccount = bankAccounts.find(
                    ({ id }) => id === accountId,
                );

                onChange({
                    financialAccountId: accountId ?? undefined,
                    entityId: selectedBankAccount?.entityId ?? undefined,
                });
            }}
            placeholder="Select account"
            renderValue={() =>
                selectedAccount ? (
                    <AccountOptionContent account={selectedAccount} />
                ) : undefined
            }
            trigger={
                <Picker.Button
                    variant="primary"
                    className={styles.accountSelect}
                />
            }
        />
    );
};
